import type { ConsentLink } from './registration.js';

/**
 * Согласия и удаление персональных данных (концепция v0.3, п. 5; docs/PRIVACY.md).
 *
 * Согласие — не галочка в строке пользователя, а журнал: когда, на что, с
 * каким текстом и в какой редакции документа человек согласился и когда
 * отозвал. Текущее состояние — последняя запись по каждому согласию.
 */

/** Где записано согласие или отзыв. */
export type ConsentSource = 'registration' | 'profile' | 'unsubscribe_link' | 'admin' | 'legacy';

export const CONSENT_SOURCE_LABELS: Record<ConsentSource, string> = {
  registration: 'при регистрации',
  profile: 'в профиле',
  unsubscribe_link: 'по ссылке «Отписаться»',
  admin: 'администратором по обращению',
  legacy: 'при регистрации, до учёта согласий',
};

/** Одна запись журнала согласий. */
export type ConsentRecord = {
  id: string;
  consentId: string;
  /** `true` — согласие дано, `false` — отозвано. */
  granted: boolean;
  /** Было ли согласие обязательным в момент записи. */
  required: boolean;
  /** Текст, под которым стояла галочка, как его видел человек. Пусто у записей до учёта согласий. */
  text: string;
  links: ConsentLink[];
  revision: string;
  source: ConsentSource;
  /** Причина или основание — только у записей администратора. */
  note: string | null;
  /** Кто из администраторов записал; `null`, если сам человек. */
  actorDisplayName: string | null;
  createdAt: string;
};

/** Согласие так, как оно действует сейчас. */
export type ConsentState = {
  consentId: string;
  /** Текст из текущей настройки регистрации; `null`, если согласие из настройки убрали. */
  text: string | null;
  links: ConsentLink[];
  required: boolean;
  granted: boolean;
  /** Последняя запись по этому согласию; `null` — записей нет. */
  latest: ConsentRecord | null;
};

export type UserConsentsView = {
  consents: ConsentState[];
  /** Новые сверху. */
  history: ConsentRecord[];
};

/**
 * Имя, которым заменяется удалённый человек в чужих партиях и журналах.
 *
 * Партия ведущего остаётся читаемой — ходы, карты, ресурсы, — но имени
 * удалённого в ней больше нет.
 */
export const ERASED_PARTICIPANT_NAME = 'Удалённый участник';

/** Что удалено: аккаунт целиком или гость в чужой партии. */
export type ErasureKind = 'user' | 'participant';

/** Кто запустил удаление: сам человек в профиле или администратор по обращению. */
export type ErasureVia = 'self' | 'admin';

/** Строка журнала удалений для администратора. Персональных данных в ней нет. */
export type ErasureLogRow = {
  kind: ErasureKind;
  via: ErasureVia;
  /** Служебный номер удалённого — по нему удаление повторяется после восстановления копии. */
  subjectId: string;
  actorDisplayName: string | null;
  at: string;
};

/** Гость в чужой партии — для удаления по обращению. */
export type GuestSearchRow = {
  participantId: string;
  displayName: string;
  sessionId: string;
  sessionTitle: string;
  sessionKind: 'spread' | 'game';
  sessionStatus: string;
  hostDisplayName: string;
  joinedAt: string;
};

/** Список для рассылки по одному необязательному согласию. */
export type MailingListSummary = {
  consentId: string;
  text: string;
  /** Согласие действует сейчас. */
  subscribed: number;
  /** Из них с подтверждённой почтой — только они попадают в выгрузку. */
  verified: number;
};
